import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import {v4 as uuidv4} from "uuid";

import { Divider, Typography } from '@mui/material';

import assets from 'src/assets';
import appRoutes from 'src/routes/AppRoutes';
import SidebarItemCollapse from '../../SidebarItemCollapse';
import SidebarItem from '../../SidebarItem';
import sizeConfigs from 'src/configs/sizeConfigs';
import theme from 'src/theme';
import StateContext from 'src/context/UserProvider';
import { useAuthMe } from 'src/context/AuthMeContext';
import { useTranslation } from 'react-i18next';

/**
 * @type Anchor
 * @typedef Posición que de va a renderizar: 'top' | 'left' | 'bottom' | 'right'
 * @private
 */
type Anchor = 'top' | 'left' | 'bottom' | 'right';

/**
 * @description Interface de posición del panel
 * @param {Anchor} pPosition - Posición de donde va a salir el menú ('top', 'left' (default), 'bottom', 'right').
 * @private
 */
interface IpropsSliderNavDrawer {
    pPosition?: Anchor;
}

/**
 * @function SliderNavDrawer
 * @description Menú colapsable (Se oculta completamente)
 * La imagen de la aplicación debe reemplazarse la que se encuentra en assets.images.logo
 * @param {Anchor} $pPosition - Posición de donde va a salir el menú ('top', 'left' (default), 'bottom', 'right').
 * @returns {React.JSX.Element}
 * @example
 *    <SliderNavDrawer pPosition='top' />
 *    <SliderNavDrawer pPosition='left' />
 *    <SliderNavDrawer pPosition='right' />
 *    <SliderNavDrawer pPosition='bottom' />
 *
 * Por defecto si no se envia el parametro la posicion por defecto es left.
 *    <SliderNavDrawer />
 */
export default function SliderNavDrawer({ pPosition }: IpropsSliderNavDrawer): React.JSX.Element {
  const { existFunction } = StateContext();
  const { canAccessModule, canAccessSubModule } = useAuthMe();
  const [position, setPosition] = React.useState<Anchor>('left');
  // const { t } = useTranslation();
  const { t } = useTranslation<["Menu"]>(["Menu"]);

  React.useEffect(() => {
    setPosition(pPosition ?? 'left')
  }, [])

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false
  })

  const toggleDrawer = (anchor: Anchor, open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      setState({ ...state, [anchor]: open })
    }

  const list = (anchor: Anchor) => (
    <Box
      sx={{
        width: anchor === 'top' || anchor === 'bottom' ? 'auto' : sizeConfigs.sidebar.width,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        userSelect: "none",
        [theme.breakpoints.down("md")]: {
          width: "100vw",
        },
      }}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "10vh",
          width: "100%",
          paddingX: "1rem"
        }}
      >
        <Typography variant='h2' color="primary" sx={{ padding: "0.5rem", width: "100%" }}>
          {t("title")}
        </Typography>
      </Box>
      <Divider />
      <Box
        sx={{
          height: "74vh",
          overflow: "auto"
        }}
      >
        {
          appRoutes.map((route, index) => {
            if (!route.sidebarProps || !existFunction(route.functionId)) return null;

            // Check module-level access from auth/me (Workload, etc.)
            const moduleName = route.state;
            if (!canAccessModule(moduleName)) return null;

            if (route.child) {
              // Filter children that have sidebar props by submodule access
              const visibleChildren = route.child.filter((child) => {
                if (!child.sidebarProps) return true;
                return !child.requiredSubModule || canAccessSubModule(child.requiredSubModule);
              });
              // Hide parent if no children with sidebar are visible
              if (!visibleChildren.some(c => c.sidebarProps)) return null;
              const filteredRoute = { ...route, child: visibleChildren };
              return <SidebarItemCollapse item={filteredRoute} key={index} />;
            }

            // Check submodule access for top-level routes without children
            if (route.requiredSubModule && !canAccessSubModule(route.requiredSubModule)) {
              return null;
            }

            return <SidebarItem item={route} key={index} />;
          })
        }
      </Box>
      <Divider />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "15vh",
          width: "100%"
        }}
      >
        <img
          src={`${assets.images.logo}`}
          srcSet={`${assets.images.logo}`}
          alt="TECHINT"
          loading="lazy"
          style={{
            maxHeight: "100%",
            maxWidth: "100%"
          }}
        />
      </Box>
    </Box>
  )

  return (
    <React.Fragment key={uuidv4()}>
      <IconButton
        edge="start"
        color="inherit"
        aria-label="menu"
        sx={{
          ml: 1,
          flexGrow: 1,
          fontSize: {
            xs: "small",
            sm: "mediun",
            md: "large"
          }
        }}
        onClick={toggleDrawer(position, true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        key={uuidv4()}
        anchor={position}
        open={state[position]}
        onClose={toggleDrawer(position, false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        {list(position)}
      </Drawer>
    </React.Fragment>
  )
}
